import { EventEmitter, Injectable, inject } from '@angular/core'
import { EMPTY, Observable, throwError } from 'rxjs'
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { ScreenService } from 'src/app/shared/services'
import { DashboardApiService } from 'src/app/shared/services/api/dashboard.api.service'
import { LogService } from 'src/app/shared/services/log.service'
import { NotificationService } from 'src/app/shared/services/notification.service'
import { UtilsService } from 'src/app/shared/services/utils.service'
import { ChartConfig } from 'src/app/types/chart.model'
import { AppMatCard } from 'src/app/types/dashboard.model'
import { AdditionalConfig, DashboardDto, DashCardDto } from 'src/app/types/dto/dashboard.types.dto'
import { DashCardRequest, PatchDashboardRequest } from 'src/app/types/request/dashboard.req'
import { CardSize, CardType, ChartType, DateAxisType, Quadrant } from 'src/app/types/enums'
import { DatasourceConfigService } from 'src/app/shared/services/chart-datasource/datasource-config.service'
import { TRACKING_VALUE_FIELD_SENTINEL } from 'src/app/types/datasources/forecast-tasks.model'
import { DEFAULT_VALUE_FIELD } from 'src/app/types/constants'
import {
  FlowUnit,
  GRID_GAP_PX,
  GRID_ROW_HEIGHT_PX,
  computeLayout,
  findUnitAt,
  groupIntoFlowUnits,
  hitTestGrid,
  hitTestQuadrant,
  quadrantOffset,
  resolveInsertIndex,
  sizeForSpan,
  spanForSize,
  unitIndexToCardIndex,
} from './grid-drag.util'

export interface DropTarget {
  row: number
  colStart: number
  span: number
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private datasourceConfigService = inject(DatasourceConfigService);
  private dashboardApi = inject(DashboardApiService);
  private screen = inject(ScreenService);
  private logger = inject(LogService);
  private notiService = inject(NotificationService);
  private utils = inject(UtilsService);

  cols = 1
  displayCards: AppMatCard[] = []
  dashboard: DashboardDto | undefined
  NEW_CARD_TITLE = 'New Chart'
  NEW_METRIC_TITLE = 'New Metric'

  addChartCols = 1 // want add chart btn to be centered in remaining space on last row
  dashboardSelected = new EventEmitter<boolean>()
  private selectingDashboard = new EventEmitter<null>()

  /** set by DashboardComponent so drag hit-testing can read grid geometry */
  gridContainer: HTMLElement | null = null

  isDragging = false
  private isDraggingNew = false
  private dragCardId: number | null = null
  /** cardType of the card currently being placed - the virtual "add" card, or an existing
   *  dragged card. Only metrics can resolve to an `extraSmall` quadrant drop target */
  private draggingCardType: CardType = CardType.Chart
  dropTarget: DropTarget | null = null
  /** set alongside dropTarget while dragging a metric near a corner of a `small` cell */
  dropQuadrant: Quadrant | null = null
  /** id of the extraSmall metric currently occupying dropQuadrant, if any - dropping there swaps
   *  it into the dragged card's old slot rather than being blocked */
  private dropSwapOccupantId: number | null = null
  private dropInsertIndex: number | null = null
  /** cardId -> pixel offset to visually slide other cards out of the way while dragging */
  cardOffsets = new Map<number, { dx: number; dy: number }>()
  /** true once the + button has been clicked, showing a draggable placeholder card */
  virtualCardActive = false
  /** cardType selected via the two "add" buttons, used while the virtual card is active */
  pendingCardType: CardType = CardType.Chart
  /** true from drop until the PATCH resolves - keeps the shuffle preview/drop indicator in
   *  place instead of snapping back to the pre-drag layout while waiting on the network */
  isSaving = false
  savingCardId: number | null = null

  constructor() {
    this.screen.changed.subscribe(() => this.processChange())
  }

  initDashboard(dashBoardId: number | undefined) {
    this.selectingDashboard.emit()
    if (this.isDragging) this.endDrag(false)
    this.virtualCardActive = false

    if (dashBoardId) {
      this.getDashboard(dashBoardId)
    } else {
      this.displayCards = []
      this.dashboard = undefined
    }
  }

  refresh() {
    if (!this.dashboard) {
      this.logger.log('dashboard.service', 'refresh', null, 'no dashboard')
      throw Error('no dashboard in refresh dashboard')
    }

    this.getDashboard(this.dashboard.id)
  }

  updateCard(card: DashCardDto) {
    if (this.dashboard) {
      const dashboardCardIndex = this.dashboard.cards.findIndex((d) => d.id === card.id)
      const displayCardIndex = this.displayCards.findIndex((d) => d.id === card.id)
      if (dashboardCardIndex === -1 || displayCardIndex === -1) return

      this.dashboard.cards.splice(dashboardCardIndex, 1, card)
      this.displayCards.splice(displayCardIndex, 1, this.createDisplayCard(card))
      this.calcAddChartCols()
    }
  }

  initLoadedDashboard(board: DashboardDto) {
    this.dashboard = board
    this.processChange()
  }

  getChartConfig(cardId: number): ChartConfig {
    const card = this.displayCards.filter((c) => c.id === cardId)[0]

    if (card.chartTypeId == null || card.dataSourceId == null || card.dateAxisTypeId == null) {
      this.logger.log('dashboard.service', 'getChartConfig', null, 'Chart not initialised')
      throw new Error('Chart not initialised')
    }

    const isDateArg = this.datasourceConfigService.isDateArgument(
      card.dataSourceId,
      card.argumentField
    )

    return {
      cardTitle: card.title,

      dataSourceId: card.dataSourceId,

      dateRangeTypeId: card.dateRangeTypeId,
      dateFilterField: card.dateFilterField,
      dateFrom: card.dateFrom,
      dateTo: card.dateTo,
      includeCurrentPartialPeriod: card.includeCurrentPartialPeriod,
      includeRecordsWithNoDate: card.config?.includeRecordsWithNoDate,

      argumentField: card.argumentField,
      splitByField: card.splitByField,
      dateAxisTypeId: card.dateAxisTypeId,
      argumentFieldIsDate: isDateArg,
      // Backwards compat: cards saved before the trackingFieldValue sentinel was introduced
      // stored valueField='default' with trackingField inside additionalConfig
      valueField: card.config?.trackingField && card.valueField === DEFAULT_VALUE_FIELD
        ? TRACKING_VALUE_FIELD_SENTINEL
        : card.valueField,
      valueAggregationTypeId: card.valueAggregationTypeId,
      startingValue: card.startingValue,
      showFilteredCategoriesOnly: card.showFilteredCategoriesOnly,

      chartTypeId: card.chartTypeId,
      filters: card.filters,
      chartColour: card.chartColour,
      targetValue: card.targetValue,
      targetOverrides: card.config?.targetOverrides,

      taskMasterId: card.taskMasterId,
      taskMaster2Id: card.taskMaster2Id,
      taskCode: card.config?.taskCode,
      task1DateField: card.task1DateField,
      task2DateField: card.task2DateField,
      taskDurationAggregation: card.taskDurationAggregation,

      trackingField: card.config?.trackingField,

      excludeInactiveUsers: card.config?.excludeInactiveUsers,
    } as ChartConfig
  }

  /** metrics have no x-axis grouping/chart type, unlike getChartConfig() */
  getMetricConfig(card: AppMatCard): ChartConfig {
    if (card.dataSourceId == null) {
      this.logger.log('dashboard.service', 'getMetricConfig', null, 'Metric not initialised')
      throw new Error('Metric not initialised')
    }

    return {
      cardTitle: card.title,

      dataSourceId: card.dataSourceId,

      dateRangeTypeId: card.dateRangeTypeId,
      dateFilterField: card.dateFilterField,
      dateFrom: card.dateFrom,
      dateTo: card.dateTo,
      includeCurrentPartialPeriod: card.includeCurrentPartialPeriod,
      includeRecordsWithNoDate: card.config?.includeRecordsWithNoDate,

      // metrics do not use an x-axis; gauge metrics may still split into subvalues
      argumentField: '',
      argumentFieldIsDate: false,
      splitByField: card.splitByField,
      dateAxisTypeId: DateAxisType.Weekly,
      chartTypeId: ChartType.Line,

      valueField: card.config?.trackingField && card.valueField === DEFAULT_VALUE_FIELD
        ? TRACKING_VALUE_FIELD_SENTINEL
        : card.valueField,
      valueAggregationTypeId: card.valueAggregationTypeId,
      startingValue: card.startingValue,
      showFilteredCategoriesOnly: card.showFilteredCategoriesOnly,

      filters: card.filters,
      chartColour: card.chartColour,
      targetValue: card.targetValue,
      targetOverrides: card.config?.targetOverrides,

      taskMasterId: card.taskMasterId,
      taskMaster2Id: card.taskMaster2Id,
      taskCode: card.config?.taskCode,
      task1DateField: card.task1DateField,
      task2DateField: card.task2DateField,
      taskDurationAggregation: card.taskDurationAggregation,

      trackingField: card.config?.trackingField,

      excludeInactiveUsers: card.config?.excludeInactiveUsers,
    } as ChartConfig
  }

  processChange() {
    if (this.dashboard) {
      if (this.screen.isSmallOrLess) {
        this.cols = 1
        this.displayCards = this.processSmallScreenCards(this.dashboard.cards)
      } else if (this.screen.isXLarge) {
        this.cols = 4
        this.displayCards = this.processLargeScreenCards(this.dashboard.cards)
      } else {
        this.cols = 2
        this.displayCards = this.processMedScreenCards(this.dashboard.cards)
      }
    }

    this.calcAddChartCols()
  }

  setTitle(newName: string) {
    if (this.dashboard) {
      this.dashboard.name = newName
      this.processChange()
    }
  }

  /** groups displayCards into grid slots - normally 1 card per slot, but up to 4 `extraSmall`
   *  metrics can share one slot (a "quad group"), one per quadrant */
  get displayUnits(): FlowUnit<AppMatCard>[] {
    return groupIntoFlowUnits(this.displayCards)
  }

  calcAddChartCols() {
    let finalRowCols = 0
    this.displayUnits.forEach((u) => {
      finalRowCols += u.cols
      if (finalRowCols > this.cols) finalRowCols = u.cols
      if (finalRowCols === this.cols) finalRowCols = 0
    })

    this.addChartCols = this.cols - finalRowCols
  }

  makeCardLarger(card: AppMatCard) {
    if (this.dashboard) {
      const req = this.patchReqFromDashboard()
      const cardMatch = req.cards.filter((c) => c.id === card.id)[0]
      switch (card.size) {
        case CardSize.medium:
          cardMatch.size = CardSize.large
          break
        case CardSize.small:
          cardMatch.size = CardSize.medium
          break
      }
      this.saveChanges(req).subscribe()
    }
  }

  makeCardSmaller(card: AppMatCard) {
    if (this.dashboard) {
      const req = this.patchReqFromDashboard()
      const cardMatch = req.cards.filter((c) => c.id === card.id)[0]
      switch (card.size) {
        case CardSize.medium:
          cardMatch.size = CardSize.small
          break
        case CardSize.large:
          cardMatch.size = CardSize.medium
          break
      }
      this.saveChanges(req).subscribe()
    }
  }

  moveCard(card: AppMatCard, moveLeft = false) {
    if (this.dashboard) {
      const req = this.patchReqFromDashboard()
      const currentIndex = req.cards.findIndex((c) => c.id === card.id)
      const newIndex = moveLeft ? currentIndex - 1 : currentIndex + 1
      this.utils.arrayMove(req.cards, currentIndex, newIndex)

      this.saveChanges(req).subscribe()
    }
  }

  addCard() {
    const req = this.patchReqFromDashboard()
    req.cards.push({
      size: CardSize.medium,
      title: this.NEW_CARD_TITLE,
    })

    this.saveChanges(req).subscribe()
  }

  addCardRight(card: AppMatCard) {
    const req = this.patchReqFromDashboard()
    const matchIndex = req.cards.findIndex((c) => c.id === card.id)
    req.cards.splice(matchIndex + 1, 0, {
      size: CardSize.medium,
      title: this.NEW_CARD_TITLE,
    })

    this.saveChanges(req).subscribe()
  }

  /** metrics default to a whole `small` cell - drag a corner to shrink to `extraSmall` */
  addMetric() {
    const req = this.patchReqFromDashboard()
    req.cards.push({
      size: CardSize.small,
      title: this.NEW_METRIC_TITLE,
      additionalConfig: this.buildConfigJson(CardType.Metric, null),
    })

    this.saveChanges(req).subscribe()
  }

  addMetricRight(card: AppMatCard) {
    const req = this.patchReqFromDashboard()
    const matchIndex = req.cards.findIndex((c) => c.id === card.id)
    req.cards.splice(matchIndex + 1, 0, {
      size: CardSize.small,
      title: this.NEW_METRIC_TITLE,
      additionalConfig: this.buildConfigJson(CardType.Metric, null),
    })

    this.saveChanges(req).subscribe()
  }

  /** metrics only toggle between `small` (whole cell) and `extraSmall` (one quadrant) -
   *  no medium/large. Shrinking defaults to the top-left quadrant, leaving the rest blank */
  toggleMetricSize(card: AppMatCard) {
    if (!this.dashboard || card.cardType !== CardType.Metric) return
    const dtoCard = this.dashboard.cards.find((c) => c.id === card.id)
    if (!dtoCard) return

    if (card.size === CardSize.small) {
      this.setConfig(dtoCard, { quadrant: Quadrant.TopLeft, quadGroupId: this.nextQuadGroupId() })
    } else if (card.size === CardSize.extraSmall) {
      this.setConfig(dtoCard, { quadrant: undefined, quadGroupId: undefined })
    } else {
      return
    }

    const req = this.patchReqFromDashboard()
    const cardMatch = req.cards.find((c) => c.id === card.id)
    if (cardMatch) {
      cardMatch.size = card.size === CardSize.small ? CardSize.extraSmall : CardSize.small
    }
    this.saveChanges(req).subscribe()
  }

  removeCard(card: AppMatCard): Observable<DashboardDto> {
    const req = this.patchReqFromDashboard()
    const matchIndex = req.cards.findIndex((c) => c.id === card.id)
    // the PATCH replaces the whole card list, so splicing a missing card (-1) would delete the last one
    if (matchIndex === -1) {
      this.notiService.showError('This card is no longer on the dashboard')
      return EMPTY
    }
    req.cards.splice(matchIndex, 1)
    return this.saveChanges(req)
  }

  /** Adds a card the editor built before it existed (a Thomas proposal) to the end of the dashboard and
   *  returns the new card's id; the editor then saves the card's settings to it. */
  addDraftCard(card: AppMatCard, title: string, dashboardId: number): Observable<number> {
    if (this.dashboard?.id !== dashboardId)
      return throwError(() => new Error('The dashboard has changed. Open the chart from Thomas again.'))

    // the PATCH replaces the whole card list, so it is built from a fresh copy: the page's copy may be
    // out of date, and sending it would delete cards added elsewhere since the page loaded
    return this.dashboardApi.getDashboard(dashboardId, true).pipe(
      switchMap((fresh) => {
        const req: PatchDashboardRequest = {
          name: fresh.name,
          cards: fresh.cards.map((c) => ({ id: c.id, size: c.size, title: c.title, additionalConfig: c.additionalConfig })),
        }
        const slot = req.cards.length
        req.cards.push({ size: card.size, title, additionalConfig: JSON.stringify(card.config ?? {}) })

        return this.dashboardApi.editDashboard(dashboardId, req).pipe(
          map((board) => {
            if (this.dashboard?.id === dashboardId) this.applySavedDashboard(board, req)
            // the PATCH returns the cards in request order, so the new card is the one in the appended slot
            const id = board.cards[slot]?.id
            if (id == null || fresh.cards.some((c) => c.id === id))
              throw new Error('The new card could not be found on the dashboard. Refresh the page.')
            return id
          })
        )
      })
    )
  }


  isLastCard(index: number): boolean {
    return index == this.displayCards.length - 1
  }

  /** converts viewport coords (e.g. from a CdkDrag pointer/drop event) into grid-relative coords */
  getPointerRelativeToGrid(
    clientX: number,
    clientY: number
  ): { relX: number; relY: number; containerWidth: number; withinBounds: boolean } | null {
    if (!this.gridContainer) return null
    const rect = this.gridContainer.getBoundingClientRect()
    const relX = clientX - rect.left
    const relY = clientY - rect.top
    // no upper relY bound - dropping below the last row is a valid "append new row" drop
    const withinBounds = relX >= 0 && relX <= rect.width && relY >= 0

    return { relX, relY, containerWidth: rect.width, withinBounds }
  }

  beginDragExisting(card: AppMatCard) {
    if (this.isSaving) return
    this.isDragging = true
    this.isDraggingNew = false
    this.dragCardId = card.id
    this.draggingCardType = card.cardType
    this.dropTarget = null
    this.dropQuadrant = null
    this.dropSwapOccupantId = null
    this.dropInsertIndex = null
    this.cardOffsets.clear()
  }

  showVirtualCard(cardType: CardType = CardType.Chart) {
    if (this.isSaving) return
    this.virtualCardActive = true
    this.pendingCardType = cardType
    this.draggingCardType = cardType
    this.isDraggingNew = true
    this.dragCardId = null
    // default to the same append position the + button previously sat in, so a plain
    // click (or a drag that ends without ever moving) can still be saved there
    this.dropTarget = this.computeDefaultAppendTarget()
    this.dropQuadrant = null
    this.dropSwapOccupantId = null
    this.dropInsertIndex = this.displayCards.length
    this.cardOffsets.clear()
  }

  cancelVirtualCard() {
    this.virtualCardActive = false
    this.isDraggingNew = false
    this.dropTarget = null
    this.dropQuadrant = null
    this.dropSwapOccupantId = null
    this.dropInsertIndex = null
  }

  beginDragNew() {
    if (this.isSaving) return
    this.isDragging = true
    this.isDraggingNew = true
    this.dragCardId = null
    this.draggingCardType = this.pendingCardType
    // keep the default target set by showVirtualCard() until the first drag-move recalculates it
    this.cardOffsets.clear()
  }

  private computeDefaultAppendTarget(): DropTarget {
    const units = groupIntoFlowUnits(this.displayCards)
    const layout = computeLayout(units.map((u) => ({ cols: u.cols })), this.cols)
    const last = layout[layout.length - 1]
    if (!last) return { row: 0, colStart: 0, span: this.cols }

    const nextColStart = last.colStart + last.cols
    return nextColStart >= this.cols
      ? { row: last.row + 1, colStart: 0, span: this.cols }
      : { row: last.row, colStart: nextColStart, span: this.cols - nextColStart }
  }

  /** relX/relY are pointer coords relative to the grid container's top-left */
  updateDragPosition(relX: number, relY: number, containerWidth: number) {
    if (!this.isDragging || containerWidth <= 0) return

    const allCards = this.displayCards
    const baseCards = this.isDraggingNew
      ? allCards
      : allCards.filter((c) => c.id !== this.dragCardId)

    const hit = hitTestGrid(relY, relX, containerWidth, this.cols)
    const baseUnits = groupIntoFlowUnits(baseCards)
    const baseLayout = computeLayout(baseUnits.map((u) => ({ cols: u.cols })), this.cols)
    const unitInsertIndex = resolveInsertIndex(baseLayout, hit.row, hit.colStart)

    // quadrant drops only apply to metrics landing on a span=1 slot that's either empty,
    // already holds a quad group with a free quadrant, or - if the dragged card is itself an
    // extraSmall metric with a slot of its own - holds a quad group whose occupied quadrant
    // can be swapped into (the occupant gets sent back to the dragged card's old slot)
    let quadrant: Quadrant | null = null
    let joiningUnit: FlowUnit<AppMatCard> | null = null
    let swapOccupantId: number | null = null
    if (this.draggingCardType === CardType.Metric && hit.span === 1) {
      const candidate = hitTestQuadrant(relY, relX, containerWidth, this.cols)
      if (candidate !== null) {
        const existingUnit = findUnitAt(baseLayout, baseUnits, hit.row, hit.colStart)
        const isQuadGroup = existingUnit?.cards[0]?.size === CardSize.extraSmall
        const occupant = existingUnit?.cards.find((c) => c.quadrant === candidate) ?? null
        const draggedCard = this.dragCardId != null ? allCards.find((c) => c.id === this.dragCardId) : undefined
        const canSwap = draggedCard?.size === CardSize.extraSmall && draggedCard.quadrant != null
        if ((!existingUnit || isQuadGroup) && (occupant == null || canSwap)) {
          quadrant = candidate
          joiningUnit = existingUnit && isQuadGroup ? existingUnit : null
          swapOccupantId = occupant?.id ?? null
        }
      }
    }

    this.dropTarget = hit
    this.dropQuadrant = quadrant
    this.dropSwapOccupantId = swapOccupantId
    this.dropInsertIndex = unitIndexToCardIndex(baseUnits, unitInsertIndex)

    const currentUnits = groupIntoFlowUnits(allCards)
    const currentLayout = computeLayout(currentUnits.map((u) => ({ cols: u.cols })), this.cols)

    // joining an existing quad group doesn't shuffle anything else - the dragged card just
    // slots into that unit's free quadrant. Otherwise it's inserted as a new unit
    const hypotheticalUnits = joiningUnit
      ? baseUnits
      : (() => {
          const units = [...baseUnits]
          units.splice(unitInsertIndex, 0, { cols: hit.span, cards: [] })
          return units
        })()
    const hypotheticalLayout = computeLayout(
      hypotheticalUnits.map((u) => ({ cols: u.cols })),
      this.cols
    )

    const currentIndexByCardId = new Map<number, number>()
    currentUnits.forEach((u, i) => u.cards.forEach((c) => currentIndexByCardId.set(c.id, i)))
    const hypoIndexByCardId = new Map<number, number>()
    hypotheticalUnits.forEach((u, i) => u.cards.forEach((c) => hypoIndexByCardId.set(c.id, i)))

    const colWidth = containerWidth / this.cols
    this.cardOffsets.clear()

    baseUnits.forEach((unit) => {
      const repId = unit.cards[0]?.id
      if (repId == null) return
      const oldIndex = currentIndexByCardId.get(repId)
      const newIndex = hypoIndexByCardId.get(repId)
      if (oldIndex == null || newIndex == null) return
      const oldEntry = currentLayout[oldIndex]
      const newEntry = hypotheticalLayout[newIndex]
      if (!oldEntry || !newEntry) return
      if (oldEntry.row === newEntry.row && oldEntry.colStart === newEntry.colStart) return

      const dx = (newEntry.colStart - oldEntry.colStart) * colWidth
      const dy = (newEntry.row - oldEntry.row) * (GRID_ROW_HEIGHT_PX + GRID_GAP_PX)
      unit.cards.forEach((c) => this.cardOffsets.set(c.id, { dx, dy }))
    })

    // a swapped-out occupant's group doesn't change position, only its own quadrant does - so
    // it needs its own preview offset, sliding it into the dragged card's old slot
    if (swapOccupantId != null && this.dragCardId != null) {
      const rowHeight = GRID_ROW_HEIGHT_PX + GRID_GAP_PX
      const draggedUnitIndex = currentUnits.findIndex((u) => u.cards.some((c) => c.id === this.dragCardId))
      const draggedCard = currentUnits[draggedUnitIndex]?.cards.find((c) => c.id === this.dragCardId)
      const draggedEntry = currentLayout[draggedUnitIndex]
      const occupantUnitIndex = baseUnits.findIndex((u) => u.cards.some((c) => c.id === swapOccupantId))
      const occupantCard = baseUnits[occupantUnitIndex]?.cards.find((c) => c.id === swapOccupantId)
      const occupantEntry = baseLayout[occupantUnitIndex]

      if (draggedEntry && occupantEntry && draggedCard?.quadrant != null && occupantCard?.quadrant != null) {
        const draggedQuadOffset = quadrantOffset(draggedCard.quadrant, colWidth)
        const occupantQuadOffset = quadrantOffset(occupantCard.quadrant, colWidth)
        const dx =
          (draggedEntry.colStart - occupantEntry.colStart) * colWidth +
          (draggedQuadOffset.x - occupantQuadOffset.x)
        const dy =
          (draggedEntry.row - occupantEntry.row) * rowHeight + (draggedQuadOffset.y - occupantQuadOffset.y)
        this.cardOffsets.set(swapOccupantId, { dx, dy })
      }
    }
  }

  getDropIndicatorRect(
    containerWidth: number
  ): { top: number; left: number; width: number; height: number } | null {
    if (!this.dropTarget || containerWidth <= 0) return null
    const colWidth = containerWidth / this.cols

    const top = this.dropTarget.row * (GRID_ROW_HEIGHT_PX + GRID_GAP_PX)
    const left = this.dropTarget.colStart * colWidth
    const width = this.dropTarget.span * colWidth - GRID_GAP_PX
    const height = GRID_ROW_HEIGHT_PX

    if (this.dropQuadrant == null) {
      return { top, left, width, height }
    }

    const halfWidth = width / 2
    const halfHeight = height / 2
    const isRight =
      this.dropQuadrant === Quadrant.TopRight || this.dropQuadrant === Quadrant.BottomRight
    const isBottom =
      this.dropQuadrant === Quadrant.BottomLeft || this.dropQuadrant === Quadrant.BottomRight

    return {
      top: top + (isBottom ? halfHeight : 0),
      left: left + (isRight ? halfWidth : 0),
      width: halfWidth,
      height: halfHeight,
    }
  }

  endDrag(commit: boolean, draggedCard?: AppMatCard) {
    if (commit && this.dashboard && this.dropTarget) {
      // a single-column layout has no resize boundaries - reordering only, size is left as-is
      const canResize = this.cols > 1
      const rawSize = canResize ? sizeForSpan(this.dropTarget.span, this.cols) : undefined
      const isMetric = this.draggingCardType === CardType.Metric
      const quadrant = isMetric ? this.dropQuadrant : null
      const size = quadrant !== null ? CardSize.extraSmall : rawSize
      const req = this.patchReqFromDashboard()
      const insertIndex = this.dropInsertIndex ?? req.cards.length
      let queued = false

      if (this.isDraggingNew) {
        req.cards.splice(insertIndex, 0, {
          size: size ?? (isMetric ? CardSize.small : CardSize.medium),
          title: isMetric ? this.NEW_METRIC_TITLE : this.NEW_CARD_TITLE,
          additionalConfig: isMetric
            ? this.buildConfigJson(CardType.Metric, quadrant, this.resolveQuadGroupId(quadrant))
            : undefined,
        })
        queued = true
      } else if (draggedCard) {
        const fromIndex = req.cards.findIndex((c) => c.id === draggedCard.id)
        if (fromIndex > -1) {
          // capture the dragged card's own slot before it's overwritten below, so a displaced
          // quadrant occupant (on a swap) can be sent there instead of just vanishing
          const originalQuadrant = draggedCard.quadrant ?? null
          const originalQuadGroupId = draggedCard.quadGroupId

          const [moved] = req.cards.splice(fromIndex, 1)
          if (size) moved.size = size
          if (isMetric) {
            moved.additionalConfig = this.mergeLayoutConfigJson(
              moved.additionalConfig,
              CardType.Metric,
              quadrant,
              this.resolveQuadGroupId(quadrant, draggedCard.id)
            )
          }
          req.cards.splice(insertIndex, 0, moved)
          queued = true

          if (this.dropSwapOccupantId != null) {
            const occupantReq = req.cards.find((c) => c.id === this.dropSwapOccupantId)
            if (occupantReq) {
              occupantReq.additionalConfig = this.mergeLayoutConfigJson(
                occupantReq.additionalConfig,
                CardType.Metric,
                originalQuadrant,
                originalQuadGroupId
              )
            }
          }
        }
      }

      if (queued) {
        // keep the shuffle preview, drop indicator and hidden dragged/virtual card in place
        // until the save resolves, instead of snapping back to the pre-drag layout meanwhile
        this.isDragging = false
        this.isSaving = true
        this.savingCardId = draggedCard?.id ?? null
        this.saveChanges(req)
          .pipe(finalize(() => this.finishDrag()))
          .subscribe()
        return
      }
    }

    this.finishDrag()
  }

  private finishDrag() {
    this.isDragging = false
    this.isDraggingNew = false
    this.dragCardId = null
    this.dropTarget = null
    this.dropQuadrant = null
    this.dropSwapOccupantId = null
    this.dropInsertIndex = null
    this.cardOffsets.clear()
    this.virtualCardActive = false
    this.isSaving = false
    this.savingCardId = null
  }

  // currently saving on every change
  saveChanges(req: PatchDashboardRequest): Observable<DashboardDto> {
    if (this.dashboard) {
      return this.dashboardApi.editDashboard(this.dashboard.id, req).pipe(
        tap((res) => {
          this.applySavedDashboard(res, req)
          this.notiService.showSuccess('Dashboard updated')
        }),
        catchError((err) => {
          this.notiService.handleError(err)
          this.logger.log('dashboard.service', 'saveChanges', err)
          throw err
        })
      )
    }
    throw new Error('no dash in saveChanges')
  }

  // only refresh card content if relevant changes have been made to it
  private applySavedDashboard(board: DashboardDto, req: PatchDashboardRequest): void {
    const currentCardsById = new Map(this.dashboard?.cards.map((card) => [card.id, card]))
    const displayCardsById = new Map(this.displayCards.map((card) => [card.id, card]))
    const changedCardIds = new Set(
      req.cards
        .filter((card) => {
          if (card.id == null) return true
          const current = currentCardsById.get(card.id)
          return !current
            || current.size !== card.size
            || current.title !== card.title
            || current.additionalConfig !== card.additionalConfig
        })
        .map((card) => card.id)
        .filter((id): id is number => id != null)
    )

    this.dashboard = board
    this.displayCards = board.cards.map((card) => {
      const existing = displayCardsById.get(card.id)
      return existing && !changedCardIds.has(card.id) ? existing : this.createDisplayCard(card)
    })
    this.calcAddChartCols()
  }

  private getDashboard(dashBoardId: number) {
    this.dashboardApi
      .getDashboard(dashBoardId)
      .pipe(takeUntil(this.selectingDashboard))
      .subscribe({
        next: (board) => {
          this.dashboard = board
          this.processChange()
          this.dashboardSelected.emit(true)
          this.calcAddChartCols()
        },
        error: (err) => {
          this.dashboardSelected.emit(false)
          this.logger.log('dashboard.service', 'initDashboard', err)
        },
      })
  }

  private patchReqFromDashboard(): PatchDashboardRequest {
    if (!this.dashboard) {
      this.logger.log(
        'dashboard.service',
        'saveChanges',
        null,
        'no dashboard in patchReqFromDashboard'
      )
      throw new Error('no dashboard in patchReqFromDashboard')
    }

    const cardsReq: DashCardRequest[] = []
    // additionalConfig is round-tripped on every patch (including plain move/resize) so it
    // isn't wiped server-side - see notes on AdditionalConfig / DashCardRequest
    this.dashboard.cards.forEach((c) =>
      cardsReq.push({ id: c.id, size: c.size, title: c.title, additionalConfig: c.additionalConfig })
    )
    return { name: this.dashboard.name, cards: cardsReq }
  }

  private getConfig(card: DashCardDto): AdditionalConfig {
    return JSON.parse(card.additionalConfig || '{}') as AdditionalConfig
  }

  private setConfig(card: DashCardDto, patch: Partial<AdditionalConfig>) {
    card.additionalConfig = JSON.stringify({ ...this.getConfig(card), ...patch })
  }

  private buildConfigJson(
    cardType: CardType,
    quadrant: Quadrant | null,
    quadGroupId?: number
  ): string {
    const config: AdditionalConfig = { cardType }
    if (quadrant !== null) {
      config.quadrant = quadrant
      config.quadGroupId = quadGroupId
    }
    return JSON.stringify(config)
  }

  private mergeLayoutConfigJson(
    additionalConfig: string | undefined,
    cardType: CardType,
    quadrant: Quadrant | null,
    quadGroupId?: number
  ): string {
    const config = JSON.parse(additionalConfig || '{}') as AdditionalConfig
    config.cardType = cardType

    if (quadrant === null) {
      delete config.quadrant
      delete config.quadGroupId
    } else {
      config.quadrant = quadrant
      config.quadGroupId = quadGroupId
    }

    return JSON.stringify(config)
  }

  /** reuses the quad group already occupying the drop target's cell (if any), so joining an
   *  existing group doesn't fragment it into two groups */
  private resolveQuadGroupId(quadrant: Quadrant | null, excludeCardId?: number): number | undefined {
    if (quadrant == null || !this.dropTarget) return undefined

    const baseCards =
      excludeCardId != null
        ? this.displayCards.filter((c) => c.id !== excludeCardId)
        : this.displayCards
    const baseUnits = groupIntoFlowUnits(baseCards)
    const baseLayout = computeLayout(baseUnits.map((u) => ({ cols: u.cols })), this.cols)
    const existingUnit = findUnitAt(baseLayout, baseUnits, this.dropTarget.row, this.dropTarget.colStart)
    return existingUnit?.cards[0]?.quadGroupId ?? this.nextQuadGroupId()
  }

  private nextQuadGroupId(): number {
    return Date.now() + Math.floor(Math.random() * 1000)
  }

  private createDisplayCard(card: DashCardDto): AppMatCard {
    const cols = this.screen.isSmallOrLess
      ? 1
      : this.screen.isXLarge
      ? spanForSize(card.size, 4)
      : card.size === CardSize.large
      ? 2
      : 1

    return new AppMatCard(card, cols, 1)
  }

  private processSmallScreenCards(cards: DashCardDto[]): AppMatCard[] {
    const processedCards: AppMatCard[] = []
    cards.forEach((c) => {
      processedCards.push(new AppMatCard(c, 1, 1))
    })

    return processedCards
  }

  private processMedScreenCards(cards: DashCardDto[]): AppMatCard[] {
    const processedCards: AppMatCard[] = []
    cards.forEach((c) => {
      const cols = c.size === CardSize.large ? 2 : 1
      processedCards.push(new AppMatCard(c, cols, 1))
    })

    return processedCards
  }

  private processLargeScreenCards(cards: DashCardDto[]): AppMatCard[] {
    const processedCards: AppMatCard[] = []
    cards.forEach((c) => {
      const cols = spanForSize(c.size, 4)
      processedCards.push(new AppMatCard(c, cols, 1))
    })

    return processedCards
  }
}
